<template>
  <MarketingHeader>
    <ConfirmModal ref="confirmModal" :title=confirm.title :text=confirm.text yes="Déconnecter" no="Retour" />

    <div class="bento-item" role="region" aria-label="Connexion à LinkedIn">
      <div class="head">
        <h3>LinkedIn</h3>
        <span v-if="loaded && connections.length > 0" class="sub">{{ pageCount }} page{{ pageCount > 1 ? 's' : '' }} reliée{{ pageCount > 1 ? 's' : '' }}</span>
      </div>

      <p v-if="loadError" class="state">Les connexions n'ont pas pu être chargées.</p>
      <p v-else-if="!loaded" class="state">Chargement…</p>
      <template v-else-if="!configured">
        <p class="state">La connexion à LinkedIn n'est pas encore activée sur ce serveur : les identifiants de l'application LinkedIn n'y sont pas renseignés.</p>
        <p class="hint">Dès qu'ils le seront, ce bouton permettra de relier vos pages LinkedIn et d'en importer toutes les publications.</p>
      </template>

      <template v-else-if="connections.length === 0">
        <p class="intro">Reliez votre page LinkedIn pour retrouver ici toutes les publications déjà faites, avec leurs chiffres, sans rien ressaisir.</p>
        <p class="hint">Le Gestionnaire se contente de lire vos publications : il ne publie rien et ne modifie rien sur LinkedIn. Vous devez être administrateur de la page.</p>
        <button type="button" class="panel-button key connect" :disabled="connecting" @click="connect">{{ connecting ? 'Redirection vers LinkedIn…' : 'Connecter LinkedIn' }}</button>
        <p class="hint limit">Seules les pages LinkedIn peuvent être reliées pour l'instant. L'import d'un profil personnel n'est pas encore pris en charge.</p>
      </template>

      <template v-else>
        <div v-for="connection in connections" :key="connection.id" class="connection">
          <div class="connection-head">
            <span class="status" :class="{problem: needsReconnect(connection)}"><i class="dot"></i>{{ needsReconnect(connection) ? 'À reconnecter' : 'Connecté' }}</span>
            <span class="expiry">{{ expiryText(connection) }}</span>
            <div class="connection-actions">
              <button v-if="needsReconnect(connection) || expiresSoon(connection)" type="button" class="panel-button" :class="{key: needsReconnect(connection)}" :disabled="connecting" @click="connect">Reconnecter</button>
              <button type="button" class="panel-button danger" @click="disconnect(connection)">Déconnecter</button>
            </div>
          </div>

          <ul class="pages">
            <li v-for="page in connection.pages" :key="page.id" class="page">
              <div class="page-row">
                <div class="page-main">
                  <strong class="page-name" :title="page.name">{{ page.name }}</strong>
                  <small>{{ pageSubtitle(page) }}</small>
                </div>
                <button type="button" class="panel-button" :class="{key: !page.lastImportAt && !needsReconnect(connection)}" :disabled="busy || needsReconnect(connection)" @click="importPage(connection, page)">{{ importLabel(page) }}</button>
              </div>

              <p v-if="importing === page.id" class="importing" role="status">Lecture des publications sur LinkedIn… cela peut prendre quelques secondes.</p>

              <div v-if="page.lastImport && importing !== page.id" class="result">
                <div class="kpis">
                  <div v-for="item in resultItems(page.lastImport)" :key="item.label" class="kpi" :title="item.help">
                    <span class="kpi-count">{{ item.count }}</span>
                    <span class="kpi-label">{{ item.label }}</span>
                  </div>
                </div>
                <p v-for="note in resultNotes(page.lastImport)" :key="note.text" class="note" :class="note.level">{{ note.text }}</p>
                <div v-if="justImported === page.id && page.lastImport.created > 0" class="classify">
                  <span>{{ page.lastImport.created > 1 ? 'Ces publications sont' : 'Cette publication est' }} à classer : indiquez à quoi {{ page.lastImport.created > 1 ? 'elles servent' : 'elle sert' }} pour {{ page.lastImport.created > 1 ? 'les' : 'la' }} compter dans votre répartition.</span>
                  <button type="button" class="panel-button key" @click="$router.push({path: '/marketing', query: {filtre: 'a-classer'}})">Classer</button>
                </div>
              </div>
            </li>
          </ul>
        </div>
        <p class="hint limit">Pour mettre à jour les chiffres, relancez l'import avec « Actualiser » : les publications déjà importées ne sont pas dupliquées, et celles que vous avez supprimées ne reviennent pas. Seules les pages LinkedIn sont prises en charge pour l'instant.</p>
      </template>
    </div>
  </MarketingHeader>
</template>

<script>
import MarketingHeader from "@/marketing/components/MarketingHeader.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {ConnectionController} from "@/marketing/controllers/ConnectionController";
import {formatDate, formatDateTime} from "@/marketing/utils";

// Un accès LinkedIn dure 60 jours : on propose de reconnecter quand il reste moins de 10 jours
const SOON_MS = 10 * 24 * 3600 * 1000;

// Message affiché au retour de LinkedIn (le service renvoie ?linkedin=<résultat> dans l'adresse)
const RETURN_MESSAGES = {
  connected: {type: "success", text: "LinkedIn est connecté : vous pouvez importer les publications de votre page"},
  cancelled: {type: "info", text: "Connexion à LinkedIn annulée"},
  nopage: {type: "danger", text: "Aucune page LinkedIn dont vous êtes administrateur n'a été trouvée avec ce compte LinkedIn"},
  forbidden: {type: "danger", text: "LinkedIn a refusé l'accès aux pages : l'application LinkedIn n'a pas encore la permission de lire les pages"},
  invalid: {type: "warning", text: "Le retour de LinkedIn a expiré ou n'est pas valide : recommencez la connexion"},
  error: {type: "danger", text: "La connexion à LinkedIn a échoué : réessayez dans un moment"},
};

const plural = (count, singular, pluralForm) => `${count} ${count > 1 ? pluralForm : singular}`;

/**
 * Écran « Connexions » : relier une page LinkedIn au Gestionnaire (autorisation chez LinkedIn, puis retour ici) et importer
 * toutes les publications déjà faites. Relancer l'import (« Actualiser ») met à jour les chiffres sans créer de doublon.
 */
export default {
  name: 'ConnectionView',
  components: {MarketingHeader, ConfirmModal},
  data() {
    return {
      loaded: false,
      loadError: false,
      configured: true,
      connections: [],
      connecting: false,
      importing: '',          // Identifiant de la page dont l'import est en cours
      justImported: '',       // Page qui vient d'être importée (propose de classer les nouveautés)
      confirm: {title: '', text: ''},
    };
  },
  computed: {
    busy() {
      return this.importing !== '' || this.connecting;
    },
    pageCount() {
      return this.connections.reduce((sum, connection) => sum + connection.pages.length, 0);
    },
  },
  async mounted() {
    this.announceReturn();
    await this.load();
  },
  methods: {
    notify(type, text) {
      (new NotificationService).addNotification(type, text);
    },
    // Retour de LinkedIn : message, puis l'adresse est nettoyée (un rechargement ne le répète pas)
    announceReturn() {
      const result = this.$route?.query?.linkedin;
      if (!result) return;
      const message = RETURN_MESSAGES[result];
      if (message) this.notify(message.type, message.text);
      this.$router.replace({path: this.$route.path, query: {}});
    },
    async load() {
      const res = await ConnectionController.getAll();
      this.loadError = !res || !!res.errors;
      if (!this.loadError) {
        this.configured = res.configured !== false;
        this.connections = res.connections ?? [];
      }
      this.loaded = true;
    },
    needsReconnect(connection) {
      return connection.status !== 'CONNECTED';
    },
    expiresSoon(connection) {
      return !!connection.expiresAt && new Date(connection.expiresAt).getTime() - Date.now() < SOON_MS;
    },
    expiryText(connection) {
      if (this.needsReconnect(connection)) return "L'accès à LinkedIn a expiré ou a été retiré : autorisez-le de nouveau.";
      return connection.expiresAt ? `Accès autorisé jusqu'au ${formatDate(connection.expiresAt)}` : '';
    },
    pageSubtitle(page) {
      const parts = [];
      if (page.vanityName) parts.push(`linkedin.com/company/${page.vanityName}`);
      parts.push(page.lastImportAt ? `dernier import le ${formatDateTime(page.lastImportAt)}` : "jamais importée");
      return parts.join(' · ');
    },
    importLabel(page) {
      if (this.importing === page.id) return 'Import en cours…';
      return page.lastImportAt ? 'Actualiser' : 'Importer les publications';
    },
    resultItems(result) {
      return [
        {label: result.found > 1 ? 'Trouvées' : 'Trouvée', count: result.found, help: "Publications lues sur LinkedIn lors du dernier import"},
        {label: result.created > 1 ? 'Nouvelles' : 'Nouvelle', count: result.created, help: "Publications créées dans le Gestionnaire"},
        {label: result.updated > 1 ? 'Actualisées' : 'Actualisée', count: result.updated, help: "Publications déjà importées dont les chiffres ont changé"},
        {label: result.unchanged > 1 ? 'Inchangées' : 'Inchangée', count: result.unchanged, help: "Publications déjà importées dont les chiffres sont les mêmes"},
      ];
    },
    resultNotes(result) {
      const notes = [];
      if (result.stats === 'FORBIDDEN') notes.push({level: 'warning', text: "LinkedIn n'autorise pas encore la lecture des chiffres (impressions, réactions…) : les publications ont été importées sans résultats. Relancez l'import quand cet accès sera accordé."});
      else if (result.stats === 'FAILED') notes.push({level: 'warning', text: "Les chiffres n'ont pas pu être lus cette fois-ci : relancez l'import plus tard pour les récupérer."});
      if (result.truncated) notes.push({level: 'warning', text: "La page compte plus de 1 000 publications : seules les 1 000 plus récentes ont été lues."});
      if (result.removedByUser > 0) notes.push({level: 'info', text: `${plural(result.removedByUser, 'publication que vous avez supprimée n\'a pas été réimportée', 'publications que vous avez supprimées n\'ont pas été réimportées')}.`});
      if (result.ignored > 0) notes.push({level: 'info', text: `${plural(result.ignored, 'brouillon ou publication sponsorisée sans existence sur la page a été ignoré', 'brouillons ou publications sponsorisées sans existence sur la page ont été ignorés')}.`});
      return notes;
    },
    async connect() {
      this.connecting = true;
      const res = await ConnectionController.linkedinAuthorizeUrl();
      if (res && res.url) {
        window.location.href = res.url;
        return;
      }
      this.connecting = false;
    },
    async importPage(connection, page) {
      if (this.busy) return;
      this.importing = page.id;
      this.justImported = '';
      const res = await ConnectionController.importPage(connection.id, page.id);
      this.importing = '';
      // Le contrôleur a déjà affiché l'erreur ; une connexion à reconnecter est relue pour que l'écran le montre
      if (!res || res.errors) {
        await this.load();
        return;
      }
      this.justImported = page.id;
      this.notify("success", res.created > 0
        ? `Import terminé : ${plural(res.created, 'nouvelle publication', 'nouvelles publications')}`
        : "Import terminé : rien de nouveau sur la page");
      await this.load();
    },
    async disconnect(connection) {
      this.confirm = {title: "Déconnecter LinkedIn", text: "Le Gestionnaire n'aura plus accès à vos pages LinkedIn. Les publications déjà importées sont conservées."};
      const res = await this.$refs.confirmModal.displayChoice(() => ConnectionController.disconnect(connection.id));
      if (res === false || (res && res.errors)) return;
      this.notify("success", "LinkedIn est déconnecté");
      this.justImported = '';
      await this.load();
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.head
  display flex
  align-items baseline
  gap 12px
  margin-bottom 8px

  h3
    margin 0
    font-size 1.05em
    text-transform uppercase
    color THEME_COLOR_PRIMARY

.sub
  color CTRL_TEXT_MUTED
  font-size 0.85em

.state, .intro
  margin 4px 0 10px

.hint
  max-width 760px

.limit
  margin-top 14px

.connect
  margin-top 6px

.connection + .connection
  margin-top 22px
  padding-top 18px
  border-top 1px solid PAGE_RULE_SOFT

.connection-head
  display flex
  flex-wrap wrap
  align-items center
  gap 8px 16px
  margin-bottom 10px

// État de la connexion : vert quand tout va bien, rouge quand il faut reconnecter
.status
  display inline-flex
  align-items center
  gap 6px
  font-weight 700
  color #2f7d4f

  .dot
    width 10px
    height 10px
    border-radius 50%
    background #2f7d4f

  &.problem
    color CTRL_ERROR

    .dot
      background CTRL_ERROR

.expiry
  flex 1 1 220px
  min-width 0
  color CTRL_TEXT_MUTED
  font-size 0.88em

.connection-actions
  display flex
  gap 8px

  .panel-button
    height 34px

.pages
  margin 0
  padding 0
  list-style none

.page
  padding 12px 0
  border-top 1px solid PAGE_RULE_SOFT

  &:first-child
    border-top none

.page-row
  display flex
  align-items center
  justify-content space-between
  gap 14px

  .panel-button
    flex none

.page-main
  display flex
  flex-direction column
  min-width 0

  small
    overflow hidden
    text-overflow ellipsis
    white-space nowrap
    color CTRL_TEXT_MUTED
    font-size 0.82em

.page-name
  overflow hidden
  text-overflow ellipsis
  white-space nowrap
  color THEME_COLOR_PRIMARY

.importing
  margin 10px 0 0
  color CTRL_TEXT_MUTED
  font-size 0.9em

.result
  margin-top 12px

.kpis
  display grid
  grid-template-columns repeat(4, minmax(0, 1fr))
  gap 10px
  max-width 640px

.kpi
  display flex
  flex-direction column
  padding 8px 12px
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  background CTRL_BG

.kpi-count
  color THEME_COLOR_PRIMARY
  font-size 1.35em
  font-weight 700

.kpi-label
  color CTRL_TEXT_MUTED
  font-size 0.82em

.note
  max-width 760px
  margin 8px 0 0
  padding 8px 12px
  border-left 4px solid THEME_COLOR_PRIMARY
  border-radius 0 10px 10px 0
  background #eef2fb
  font-size 0.9em

  &.warning
    border-left-color #b45309
    background #fdf6ea

.classify
  display flex
  align-items center
  justify-content space-between
  gap 14px
  max-width 760px
  margin-top 10px
  padding 10px 14px
  border 1px solid PAGE_LEMON
  border-radius CTRL_RADIUS
  background PAGE_LEMON_HOVER
  font-size 0.9em

  .panel-button
    flex none
    height 34px

@media (max-width 760px)
  .kpis
    grid-template-columns repeat(2, minmax(0, 1fr))

  .page-row, .classify
    flex-wrap wrap
</style>
